<template>

  <div

    class="sidebar-shell"

    :class="{ 'sidebar-shell--open': isPanelOpen }"

    :style="{

      background: theme.background,

      color: theme.text,

      '--sidebar-panel-width': `${panelWidth}px`,

      '--sidebar-total-width': `${totalSidebarWidth}px`

    }"

  >

    <!-- ======================================================

         LEVEL 1: INVENTORY & LOGISTICS MAJOR HEADINGS

    ======================================================= -->

    <v-card

      flat

      class="sidebar-rail"

      :style="{

        background: theme.primary,

        color: theme.text

      }"

    >

      <v-list

        nav

        density="compact"

        class="sidebar-rail-list"

        :style="{

          background: theme.primary,

          color: theme.text

        }"

      >

        <template v-for="item in visibleRootMenu" :key="item.id">

          <v-tooltip location="right" :text="item.title">

            <template #activator="{ props }">

              <v-list-item

                v-bind="props"

                class="sidebar-rail-item"

                :class="{ 'sidebar-rail-item--active': selectedRootId === item.id }"

                :active="selectedRootId === item.id"

                @click="selectRootItem(item)"

              >

                <template #default>

                  <div class="d-flex justify-center align-center w-100">

                    <v-icon :icon="item.icon || 'mdi-circle-outline'" size="24" />

                  </div>

                </template>

              </v-list-item>

            </template>

          </v-tooltip>

        </template>

      </v-list>

    </v-card>

    <!-- ======================================================

         LEVEL 2: SEARCHABLE SLIDING MENU

    ======================================================= -->

    <v-slide-x-transition>

      <v-card

        v-if="isPanelOpen && selectedRoot"

        flat

        class="sidebar-panel"

        :style="{

          background: theme.background,

          color: theme.text,

          width: `${panelWidth}px`,

          minWidth: `${panelWidth}px`,

          maxWidth: `${panelWidth}px`

        }"

      >

        <!-- PANEL HEADER -->

        <div

          class="sidebar-panel-header"

          :style="{

            background: theme.primary,

            color: theme.text

          }"

        >

          <div class="d-flex align-center ga-2 min-width-0">

            <v-icon :icon="selectedRoot.icon || 'mdi-circle-outline'" size="22" />

            <div class="sidebar-panel-title">

              {{ selectedRoot.title }}

            </div>

          </div>

          <v-tooltip

            location="bottom"

            :text="currentLanguage === 'fr' ? 'Fermer le menu' : 'Close menu'"

          >

            <template #activator="{ props }">

              <v-btn

                v-bind="props"

                icon="mdi-chevron-left"

                variant="text"

                size="small"

                @click="closePanel"

              />

            </template>

          </v-tooltip>

        </div>

        <!-- SEARCH -->

        <div class="sidebar-search">

          <v-text-field

            v-model="search"

            :label="currentLanguage === 'fr' ? 'Rechercher' : 'Search'"

            variant="outlined"

            density="compact"

            prepend-inner-icon="mdi-magnify"

            clearable

            hide-details

          />

        </div>

        <v-divider />

        <!-- CHILD MENU -->

        <div class="sidebar-panel-content">

          <v-list

            nav

            density="compact"

            class="sidebar-child-list"

            :style="{

              background: theme.background,

              color: theme.text

            }"

          >

            <v-list-item

              v-for="child in filteredSelectedChildren"

              :key="child.id"

              :prepend-icon="child.icon || 'mdi-circle-small'"

              :title="child.title"

              link

              :to="child.to"

              :class="{ 'active-item': isRouteActive(child.to) }"

            />

            <div

              v-if="filteredSelectedChildren.length === 0"

              class="sidebar-empty"

            >

              <v-icon

                icon="mdi-magnify-close"

                size="28"

                class="mb-2"

              />

              <div>

                {{

                  currentLanguage === "fr"

                    ? "Aucun menu trouvé"

                    : "No menu items found"

                }}

              </div>

            </div>

          </v-list>

        </div>

        <!-- RESIZE HANDLE -->

        <div

          class="sidebar-resize-handle"

          :class="{ 'sidebar-resize-handle--active': isResizing }"

          @pointerdown="startResize"

        >

          <div class="sidebar-resize-grip" />

        </div>

      </v-card>

    </v-slide-x-transition>

  </div>

</template>

<script setup>

import { computed, onBeforeUnmount, ref, watch } from "vue"

import { useRoute, useRouter } from "vue-router"

import { useThemeStore } from "@/store/auth"

import { useLangStore } from "@/store/lang"

const route = useRoute()

const router = useRouter()

const langStore = useLangStore()

const themeStore = useThemeStore()

const theme = computed(() => themeStore.themeStyles)

const currentLanguage = computed(() => langStore.currentLanguage || "en")

const lang = computed(() => langStore.currentLanguage)

const emit = defineEmits(["resize", "panel-state"])

const search = ref("")
const open = ref([])

const selectedRootId = ref("Dashboard")

const isPanelOpen = ref(true)

// ============================================================

// SIDEBAR DIMENSIONS

// ============================================================

const ICON_RAIL_WIDTH = 64

const DEFAULT_PANEL_WIDTH = 280

const MIN_PANEL_WIDTH = 220

const MAX_PANEL_WIDTH = 520

const panelWidth = ref(DEFAULT_PANEL_WIDTH)

const isResizing = ref(false)

const resizeStartX = ref(0)

const resizeStartWidth = ref(DEFAULT_PANEL_WIDTH)

const totalSidebarWidth = computed(() => isPanelOpen.value ? ICON_RAIL_WIDTH + panelWidth.value : ICON_RAIL_WIDTH)

// ============================================================

// INVENTORY & LOGISTICS MENU | Tsl

// ============================================================

const menu = computed(() => [

  // ==========================================================

  // DASHBOARD

  // ==========================================================

  { id: "Dashboard", title: currentLanguage.value === "fr" ? "Tableau de bord" : "Dashboard", icon: "mdi-view-dashboard-outline", 

  children: [

    { id: "dashboard-overview", title: currentLanguage.value === "fr" ? "Vue d'ensemble" : "Dashboard Overview", icon: "mdi-view-dashboard", to: resolveRoute("InventoryLogisticsDashboard") }

  ] },

  // ==========================================================

  // INVENTORY

  // ==========================================================

  { id: "Inventory", title: currentLanguage.value === "fr" ? "Inventaire" : "Inventory", icon: "mdi-package-variant-closed", 

  children: [

    { id: "inventory-items", title: currentLanguage.value === "fr" ? "Articles" : "Items", icon: "mdi-package-variant", to: resolveRoute("ItemMasterPage") },

    { id: "inventory-categories", title: currentLanguage.value === "fr" ? "Catégories d'articles" : "Item Categories", icon: "mdi-shape-outline", to: resolveRoute("ItemCategoriesPage") },

    { id: "inventory-lots", title: currentLanguage.value === "fr" ? "Lots d'inventaire" : "Inventory Lots", icon: "mdi-package-variant-closed-check", to: resolveRoute("InventoryLotPage") },

    { id: "inventory-warehouses", title: currentLanguage.value === "fr" ? "Entrepôts" : "Warehouses", icon: "mdi-warehouse", to: resolveRoute("WarehousePage") }

  ] },

   { id: "SuppliersVendors", title: currentLanguage.value === "fr" ? "Supplier & Vendors" : "Supplier & Vendors", icon: "mdi-package-variant-closed", 

  children: [

    { id: "osetup-4", title: 'Vendor creation', icon: "mdi-image-area", to: router.resolve({ name: lang.value === 'en' ? "VendorEN" : "VendorFR"}).href },

  ] },

  // ==========================================================

  // STOCK OPERATIONS

  // ==========================================================

  { id: "StockOperations", title: currentLanguage.value === "fr" ? "Opérations de stock" : "Stock Operations", icon: "mdi-swap-horizontal-bold", 

  children: [

    { id: "stock-receipt", title: currentLanguage.value === "fr" ? "Réception de stock" : "Receive Stock", icon: "mdi-tray-arrow-down", to: resolveRoute("StockReceiptPage") },

    { id: "stock-issue", title: currentLanguage.value === "fr" ? "Sortie de stock" : "Issue Stock", icon: "mdi-tray-arrow-up", to: resolveRoute("StockIssuePage") },

    { id: "stock-return", title: currentLanguage.value === "fr" ? "Retour de stock" : "Return Stock", icon: "mdi-keyboard-return", to: resolveRoute("StockReturnPage") },

    { id: "stock-transfer", title: currentLanguage.value === "fr" ? "Transfert de stock" : "Transfer Stock", icon: "mdi-swap-horizontal", to: resolveRoute("StockTransferPage") },

    { id: "stock-adjustment", title: currentLanguage.value === "fr" ? "Ajustement de stock" : "Stock Adjustment", icon: "mdi-tune-variant", to: resolveRoute("StockAdjustmentPage") }

  ] },

  {
        id: "Operations/POS",
        title: 'POS',
        children: [
          { id: "pscsec-10", title: 'POS Terminal Location', icon: "mdi-monitor-settings", to: router.resolve({ name: lang.value === 'en' ? "TerminalLocationEN" : "TerminalLocationFR"}).href },

        ]
      },

  // ==========================================================

  // INVENTORY CONTROL

  // ==========================================================

  { id: "InventoryControl", title: currentLanguage.value === "fr" ? "Contrôle d'inventaire" : "Inventory Control", icon: "mdi-shield-check-outline", children: [

    { id: "control-quarantine", title: currentLanguage.value === "fr" ? "Lots en quarantaine" : "Quarantined Lots", icon: "mdi-lock-alert-outline", to: resolveRoute("InventoryQuarantinePage") },

    { id: "control-recalled", title: currentLanguage.value === "fr" ? "Lots rappelés" : "Recalled Lots", icon: "mdi-alert-octagon-outline", to: resolveRoute("InventoryRecallPage") },

    { id: "control-expiring", title: currentLanguage.value === "fr" ? "Lots bientôt expirés" : "Expiring Lots", icon: "mdi-calendar-clock-outline", to: resolveRoute("InventoryExpiringLotsPage") },

    { id: "control-expired", title: currentLanguage.value === "fr" ? "Lots expirés" : "Expired Lots", icon: "mdi-calendar-remove-outline", to: resolveRoute("InventoryExpiredLotsPage") }

  ] },

  // ==========================================================

  // REPORTS

  // ==========================================================

  { id: "Reports", title: currentLanguage.value === "fr" ? "Rapports" : "Reports", icon: "mdi-chart-box-outline", children: [

    { id: "reports-stock-movements", title: currentLanguage.value === "fr" ? "Mouvements de stock" : "Stock Movements", icon: "mdi-chart-timeline-variant", to: resolveRoute("StockMovementPage") },

    { id: "reports-valuation", title: currentLanguage.value === "fr" ? "Valorisation de l'inventaire" : "Inventory Valuation", icon: "mdi-cash-multiple", to: resolveRoute("InventoryValuationPage") },

    { id: "reports-warehouse", title: currentLanguage.value === "fr" ? "Inventaire par entrepôt" : "Warehouse Inventory", icon: "mdi-warehouse", to: resolveRoute("WarehouseInventoryPage") },

    { id: "reports-stock", title: currentLanguage.value === "fr" ? "Rapports de stock" : "Stock Reports", icon: "mdi-file-chart-outline", to: resolveRoute("StockReportsPage") }

  ] },

  // ==========================================================

  // BARCODES & SCANNING

  // ==========================================================

  { id: "Barcodes", title: currentLanguage.value === "fr" ? "Codes-barres" : "Barcodes", icon: "mdi-barcode-scan", children: [

    { id: "barcode-management", title: currentLanguage.value === "fr" ? "Gestion des codes-barres" : "Barcode Management", icon: "mdi-barcode", to: resolveRoute("BarcodePage") },

    { id: "barcode-scanner", title: currentLanguage.value === "fr" ? "Scanner de codes-barres" : "Barcode Scanner", icon: "mdi-barcode-scan", to: resolveRoute("BarcodeScannerPage") }

  ] }

])

// ============================================================

// ROUTE RESOLUTION

// ============================================================

function resolveRoute(name) {

  if (!name) return null

  try {

    return router.resolve({ name }).href

  }

  catch {

    return null

  }

}

// ============================================================

// ROLE-BASED MENU VISIBILITY

// Permissions are assigned to roles and enforced by the backend.

// The Inventory sidebar presents the module menu for the authenticated

// user's assigned role context, just like the Admin sidebar.

// ============================================================

const visibleRootMenu = computed(() => menu.value)

const selectedRoot = computed(() => {

  return visibleRootMenu.value.find(item => String(item.id) === String(selectedRootId.value)) ?? null

})

// ============================================================

// SEARCH

// ============================================================

const filteredSelectedChildren = computed(() => {

  if (!selectedRoot.value) return []

  const query = String(search.value ?? "").trim().toLowerCase()

  if (!query) {

    return selectedRoot.value.children ?? []

  }

  return (selectedRoot.value.children ?? []).filter(item => {

    return String(item.title ?? "").toLowerCase().includes(query)

  })

})

// ============================================================

// ROOT SELECTION

// ============================================================

function selectRootItem(item) {

  if (!item) return

  if (selectedRootId.value === item.id && isPanelOpen.value) {

    closePanel()

    return

  }

  selectedRootId.value = item.id

  search.value = ""

  isPanelOpen.value = true

  emitSidebarWidth()

}

function closePanel() {

  isPanelOpen.value = false

  search.value = ""

  open.value = []

  emitSidebarWidth()

}

// ============================================================

// ACTIVE ROUTE

// ============================================================

function normalizePath(value) {

  if (!value) return ""

  try {

    return new URL(value, window.location.origin).pathname.replace(/\/+$/, "")

  }

  catch {

    return String(value).split("?")[0].split("#")[0].replace(/\/+$/, "")

  }

}

function isRouteActive(to) {

  if (!to) return false

  return normalizePath(route.path) === normalizePath(to)

}

function containsActiveRoute(item) {

  if (!item) return false

  if (item.to && isRouteActive(item.to)) {

    return true

  }

  return (item.children || []).some(containsActiveRoute)

}

function findRootForCurrentRoute() {

  return visibleRootMenu.value.find(root => containsActiveRoute(root)) ?? null

}

// ============================================================

// KEEP LEVEL 1 SYNCHRONIZED WITH CURRENT ROUTE

// ============================================================

watch(

  () => route.path,

  () => {

    const root = findRootForCurrentRoute()

    if (root) {

      selectedRootId.value = root.id

      isPanelOpen.value = true

    }

  },

  {

    immediate: true

  }

)

// ============================================================

// SIDEBAR WIDTH EVENT

// ============================================================

function emitSidebarWidth() {

  const detail = {

    railWidth: ICON_RAIL_WIDTH,

    panelWidth: isPanelOpen.value ? panelWidth.value : 0,

    totalWidth: totalSidebarWidth.value,

    isOpen: isPanelOpen.value

  }

  emit("resize", detail)

  emit("panel-state", detail)

  window.dispatchEvent(

    new CustomEvent("nexgbits-sidebar-resize", {

      detail

    })

  )

}

// ============================================================

// RESIZE

// ============================================================

let activeResizeTarget = null

function startResize(event) {

  if (!isPanelOpen.value) return

  if (event.pointerType === "mouse" && event.button !== 0) {

    return

  }

  event.preventDefault()

  event.stopPropagation()

  isResizing.value = true

  resizeStartX.value = Number(event?.clientX ?? 0)

  resizeStartWidth.value = panelWidth.value

  activeResizeTarget = event.currentTarget

  activeResizeTarget?.setPointerCapture?.(event.pointerId)

  document.body.classList.add("nexgbits-sidebar-resizing")

  window.addEventListener("pointermove", handleResize)

  window.addEventListener("pointerup", stopResize)

  window.addEventListener("pointercancel", stopResize)

}

function handleResize(event) {

  if (!isResizing.value) return

  event.preventDefault()

  const currentX = Number(event?.clientX ?? 0)

  const delta = currentX - resizeStartX.value

  panelWidth.value = Math.min(

    MAX_PANEL_WIDTH,

    Math.max(

      MIN_PANEL_WIDTH,

      resizeStartWidth.value + delta

    )

  )

  emitSidebarWidth()

}

function stopResize(event) {

  if (!isResizing.value) return

  isResizing.value = false

  if (activeResizeTarget && event?.pointerId != null) {

    try {

      activeResizeTarget.releasePointerCapture?.(event.pointerId)

    }

    catch {

      // Pointer already released.

    }

  }

  activeResizeTarget = null

  document.body.classList.remove("nexgbits-sidebar-resizing")

  window.removeEventListener("pointermove", handleResize)

  window.removeEventListener("pointerup", stopResize)

  window.removeEventListener("pointercancel", stopResize)

  emitSidebarWidth()

}

// ============================================================

// WATCH SIDEBAR WIDTH

// ============================================================

watch(

  totalSidebarWidth,

  () => {

    emitSidebarWidth()

  },

  {

    immediate: true

  }

)

// ============================================================

// CLEANUP

// ============================================================

onBeforeUnmount(() => {

  window.removeEventListener("pointermove", handleResize)

  window.removeEventListener("pointerup", stopResize)

  window.removeEventListener("pointercancel", stopResize)

  document.body.classList.remove("nexgbits-sidebar-resizing")

})

</script>

<style scoped>

.sidebar-shell {

  display: flex;

  align-items: stretch;

  width: 64px;

  min-height: 100%;

  overflow: visible;

  transition: width 160ms ease;

}

.sidebar-shell--open {

  width: var(--sidebar-total-width);

}

/* ============================================================

   LEVEL 1 RAIL

\============================================================ */

.sidebar-rail {

  width: 64px;

  min-width: 64px;

  border-radius: 0;

  z-index: 2;

}

.sidebar-rail-list {

  padding: 8px 6px;

}

.sidebar-rail-item {

  width: 52px;

  min-height: 48px !important;

  margin: 4px 0;

  padding: 0 !important;

  border-radius: 10px !important;

  justify-content: center;

}

.sidebar-rail-item :deep(.v-list-item__content) {

  display: flex;

  justify-content: center;

  align-items: center;

}

.sidebar-rail-item--active {

  background: rgba(255, 255, 255, 0.18) !important;

}

.sidebar-rail-item:hover {

  background: rgba(255, 255, 255, 0.12) !important;

}

/* ============================================================

   LEVEL 2 PANEL

\============================================================ */

.sidebar-panel {

  position: relative;

  flex: 0 0 auto;

  width: var(--sidebar-panel-width);

  min-width: var(--sidebar-panel-width);

  max-width: var(--sidebar-panel-width);

  border-radius: 0;

  border-right: 1px solid rgba(0, 0, 0, 0.08);

  box-shadow: 6px 0 18px rgba(0, 0, 0, 0.08);

  z-index: 1;

}

.sidebar-panel-header {

  min-height: 56px;

  padding: 8px 10px 8px 14px;

  display: flex;

  align-items: center;

  justify-content: space-between;

}

.sidebar-panel-title {

  min-width: 0;

  max-width: 185px;

  font-size: 15px;

  font-weight: 700;

  white-space: nowrap;

  overflow: hidden;

  text-overflow: ellipsis;

}

/* ============================================================

   SEARCH

\============================================================ */

.sidebar-search {

  padding: 12px;

}

/* ============================================================

   CHILD MENU

\============================================================ */

.sidebar-panel-content {

  max-height: calc(100vh - 130px);

  overflow-y: auto;

  overflow-x: hidden;

}

.sidebar-child-list {

  padding: 6px;

}

.sidebar-child-list :deep(.v-list-item) {

  min-height: 40px !important;

  border-radius: 7px;

  margin-bottom: 2px;

}

/* ============================================================

   ACTIVE ROUTE

\============================================================ */

.active-item {

  background: rgba(25, 118, 210, 0.16) !important;

  color: #1976d2 !important;

  font-weight: 700;

}

.active-item :deep(.v-icon) {

  color: #1976d2 !important;

}

/* ============================================================

   EMPTY SEARCH

\============================================================ */

.sidebar-empty {

  padding: 32px 12px;

  text-align: center;

  opacity: 0.7;

  font-size: 14px;

}

.min-width-0 {

  min-width: 0;

}

/* ============================================================

   RESIZE HANDLE

\============================================================ */

.sidebar-resize-handle {

  position: absolute;

  top: 0;

  right: 0;

  width: 12px;

  height: 100%;

  cursor: ew-resize;

  z-index: 20;

  display: flex;

  align-items: center;

  justify-content: center;

  user-select: none;

  touch-action: none;

}

.sidebar-resize-handle::before {

  content: "";

  position: absolute;

  top: 0;

  bottom: 0;

  left: 3px;

  width: 2px;

  background: rgba(0, 0, 0, 0.08);

}

.sidebar-resize-handle:hover::before,

.sidebar-resize-handle--active::before {

  width: 3px;

  background: rgba(25, 118, 210, 0.55);

}

.sidebar-resize-grip {

  width: 3px;

  height: 42px;

  border-radius: 999px;

  background: rgba(0, 0, 0, 0.18);

  opacity: 0;

}

.sidebar-resize-handle:hover .sidebar-resize-grip,

.sidebar-resize-handle--active .sidebar-resize-grip {

  opacity: 1;

}

:global(body.nexgbits-sidebar-resizing) {

  cursor: ew-resize !important;

  user-select: none !important;

}

:global(body.nexgbits-sidebar-resizing *) {

  cursor: ew-resize !important;

}

:global(body.nexgbits-sidebar-resizing) .sidebar-shell {

  transition: none !important;

}

/* ============================================================

   SMALL SCREENS

\============================================================ */

@media (max-width: 600px) {

  .sidebar-shell--open {

    width: min(var(--sidebar-total-width), 100vw);

  }

  .sidebar-panel {

    width: min(var(--sidebar-panel-width), calc(100vw - 64px));

    min-width: min(var(--sidebar-panel-width), calc(100vw - 64px));

    max-width: calc(100vw - 64px);

  }

  .sidebar-resize-handle {

    display: none;

  }

}

</style>